import React from 'react';
import isPlainObject from 'lodash/isPlainObject';
import { getFields } from '@buddy-technology/ion-helpers';

import GlobalStyles from '../components/UI/GlobalStyles';
import ViewPreviewComponent from '../components/ViewPreviewComponent';
import getInitialIonValues from '../utils/getInitialIonValues';
import prepareIon from '../utils/prepareION';
import { getQuoteRequirements } from '../utils/getters';
import {
	CallProvider,
	EventProvider,
	DataProvider,
	ThemeProvider,
} from '../context';
import { STAGES } from '../models/dictionary';

/**
 * ViewPreview is a high-level composer component that renders an ION view with complete context providers and validation.
 * It processes ION data, sets up form context, and renders a preview of a specific view with dynamic form handling.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} [props.data] - Additional data to be merged with form context and used in calculations
 * @param {Function} [props.eventCallback=() => {}] - Callback function for handling form events
 * @param {boolean} [props.includeCheckout=true] - Whether to include checkout functionality in the form
 * @param {Object} props.ion - The raw ION configuration object containing application data, views, and variables
 * @param {string} [props.partnerId] - Partner identifier for the application
 * @param {string} [props.placesApiKey] - Google Places API key for location services
 * @param {boolean} [props.showValidateButton] - Whether to display validation buttons in the form
 * @param {string} [props.stage=STAGES.LOCAL] - Application stage ('LOCAL', 'PRODUCTION', etc.)
 * @param {Object} [props.stripe] - Stripe configuration object for payment processing
 * @param {Object} [props.theme={}] - Theme configuration object for styling
 * @param {string} [props.validationMode] - Form validation mode ('onSubmit' | 'onBlur' | 'onTouched' | 'all')
 * @param {string} props.viewId - The ID of the view to render from the ION configuration
 *
 * @throws {Error} When ion prop is missing or not a plain object
 * @throws {Error} When viewId prop is missing or not a string
 * @throws {Error} When the specified viewId cannot be found in the ION configuration
 *
 * @returns {React.Component} A complete form preview wrapped with all necessary context providers
 *
 * @example
 * <ViewPreview
 *   ion={ionConfig}
 *   viewId="personal-info"
 *   data={{ userId: '123' }}
 *   theme={{ primaryColor: '#007bff' }}
 *   eventCallback={(event) => console.log('Form event:', event)}
 *   includeCheckout={true}
 *   showValidateButton={true}
 *   stage="PRODUCTION"
 *   validationMode="onTouched"
 * />
 */
const ViewPreview = ({
	data,
	eventCallback = () => {},
	includeCheckout = true, // default only kicks in if undefined.
	ion: rawIon,
	partnerId,
	placesApiKey,
	showValidateButton,
	stage = STAGES.LOCAL,
	stripe,
	theme = {},
	validationMode, // onSubmit | onBlur | onTouched | all
	viewId,
}) => {
	if (!rawIon) {
		throw new Error('[ion] is required.');
	}

	if (!isPlainObject(rawIon)) {
		throw new Error(`Expected [ion] to be an ION object, but received ${rawIon}.`);
	}

	if (!viewId) {
		throw new Error('[viewId] is required.');
	}

	if (typeof viewId !== 'string') {
		throw new Error(`Expected [viewId] to be of type string but received: [${typeof viewId}].`);
	}

	const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null;
	// passing null here is ok, as it will require the helper to use computer time (as opposed to default of UTC)
	const ion = prepareIon(rawIon, { shouldConvertRuleKeys: true, timezone });

	const view = ion.application.views.find(({ id }) => viewId === id);

	if (!view) {
		throw new Error(`Cannot find view with id [${viewId}].`);
	}

	// Flatten the Ion's Application to make it easier for RHF and ITR to consume.
	const flatApplication = getFields(ion, true);

	// filtered comes without hidden fields or reference elements
	const filteredApplication = flatApplication.filter(({ uiDisplay }) => (uiDisplay.element !== 'REFERENCE' && uiDisplay.isHidden !== true));

	// Grab our fields from the content property of the view (fields is an array) and return a single array of fields for our view.
	const fields = view.content?.reduce((acc, current) => {
		if (Array.isArray(current.fields)) {
			return [...acc, ...current.fields];
		}
		return acc;
	}, []);

	// this object holds our incoming props in order to run mingo queries where needed.
	const offeringOptions = {
		data,
		includeCheckout,
		partnerId,
	};

	const	quoteRequirements = getQuoteRequirements(ion);

	const defaultValues = getInitialIonValues({
		flatApplication,
		incomingData: data,
		context: {
			variables: ion.variables,
			...data,
			offeringOptions,
		},
	});

	return (
		<GlobalStyles theme={theme} useShadow>
			<DataProvider ion={ion || {}} offeringOptions={offeringOptions}>
				<ThemeProvider theme={theme}>
					<EventProvider eventCallback={eventCallback}>
						<CallProvider>
							<ViewPreviewComponent
								defaultValues={defaultValues}
								fields={fields}
								filteredApplication={filteredApplication}
								flatApplication={flatApplication}
								includeCheckout={includeCheckout}
								ion={ion}
								partnerId={partnerId}
								placesApiKey={placesApiKey}
								quoteRequirements={quoteRequirements}
								showValidateButton={showValidateButton}
								stage={stage}
								stripe={stripe}
								validationMode={validationMode}
								view={view}
							/>
						</CallProvider>
					</EventProvider>
				</ThemeProvider>
			</DataProvider>
		</GlobalStyles>
	);
};

export default ViewPreview;
